<template>
  <div class="reading-streak-widget-full16464">
    <ReadingStreakWidget16464
      class="reading-streak-widget-full16464__main-block"
      :streakData
    >
      <div class="reading-streak-widget-full16464__calendar">
        <div
          v-for="day in daysOfTheWeek"
          :key="day"
          class="reading-streak-widget-full16464__day"
        >
          <span
            class="reading-streak-widget-full16464__day-check"
            :class="{
              'reading-streak-widget-full16464__day-check--today': isToday(day),
              'reading-streak-widget-full16464__day-check--active':
                streakDays.includes(day),
            }"
          >
            <span class="checkmark"></span>
          </span>
          <p class="reading-streak-widget-full16464__day-label">{{ day }}</p>
        </div>
      </div>
    </ReadingStreakWidget16464>

    <div class="reading-streak-widget-full16464__block">
      <p class="reading-streak-widget-full16464__block-title">Longest streak</p>
      <p class="reading-streak-widget-full16464__block-text">
        {{ longestStreakText }}
      </p>
      <ReadingStreakIcon16464
        class="reading-streak-widget-full16464__block-icon"
        :active="true"
      />
    </div>

    <div class="reading-streak-widget-full16464__block">
      <p class="reading-streak-widget-full16464__block-title">Read</p>
      <p class="reading-streak-widget-full16464__block-text">
        {{ booksReadText }}
      </p>
      <span
        class="reading-streak-widget-full16464__block-icon iconfont ibook"
      ></span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { ReadingStreakData16464 } from '@/models/interfaces'

import ReadingStreakWidget16464 from './ReadingStreakWidget16464.vue'
import ReadingStreakIcon16464 from './ReadingStreakIcon16464.vue'

const props = defineProps<{
  streakData: ReadingStreakData16464
}>()

const daysOfTheWeek = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
const currentDayIndex = new Date().getDay()

function isToday(day: string): boolean {
  return day === daysOfTheWeek[currentDayIndex]
}

const streakDays = computed(() => {
  if (props.streakData.streak_days <= 0) {
    // User has no streak.
    return []
  }

  const isCurrentDayComplete = props.streakData.remaining_seconds <= 0

  const lastStreakDay = isCurrentDayComplete
    ? currentDayIndex // Today
    : currentDayIndex - 1 // Yesterday

  const firstStreakDay = lastStreakDay - props.streakData.streak_days + 1
  const firstStreakDayThisWeek =
    firstStreakDay < 0
      ? 0 // The streak started before this week
      : firstStreakDay

  return daysOfTheWeek.slice(firstStreakDayThisWeek, lastStreakDay + 1)
})

const longestStreakText = computed(() => {
  const value = props.streakData.longest_streak_days ?? 0
  return `${value} ${value === 1 ? 'day' : 'days'}`
})

const booksReadText = computed(() => {
  const value = props.streakData.total_books_read ?? 0
  return `${value} ${value === 1 ? 'book' : 'books'}`
})
</script>

<style lang="scss" scoped>
.reading-streak-widget-full16464 {
  display: grid;
  grid-template-areas:
    'A B'
    'A C';
  gap: 16px;

  @media (max-width: $w-mobile-s) {
    grid-template-areas:
      'A A'
      'B C';
  }

  &__main-block {
    grid-area: A;
    pointer-events: none;
    @include hover {
      cursor: initial;
      --streak-border-c1: initial;
      --streak-border-c2: initial;
    }
  }

  &__block {
    display: grid;
    grid-template-columns: 1fr 28px;
    grid-template-rows: 1fr 1fr;
    width: 100%;
    padding: 12px 16px;
    border: 1px solid transparent;
    border-radius: 12px;
    background:
      linear-gradient(
          135deg,
          rgba(255, 200, 142, 1) 0%,
          rgba(255, 223, 121, 1) 100%
        )
        padding-box,
      linear-gradient(
          135deg,
          rgba(255, 231, 155, 1) 0%,
          rgba(255, 206, 155, 1) 100%
        )
        border-box;

    @media (max-width: $w-tablet-s) {
      padding: 16px;
    }
  }

  &__block-title,
  &__block-text {
    display: flex;
    align-items: center;
    min-height: 24px;
    line-height: 16px;
    letter-spacing: -0.02em;
    color: $c-black-15;
    margin: 0;
  }

  &__block-title {
    align-self: end;
    font-size: 12px;
  }

  &__block-text {
    align-self: start;
    font-size: 18px;
    font-weight: 500;
  }

  &__block-icon {
    grid-column: 2 / 3;
    grid-row: 1 / 3;
    align-self: center;
    width: 28px;
    height: 28px;

    &.iconfont {
      position: relative;
      z-index: 1;
      font-size: 29px;
      line-height: 28px;
      color: $c-black-15;

      &:after {
        content: '';
        position: absolute;
        left: 3px;
        top: 1px;
        z-index: -1;
        display: block;
        width: 16px;
        height: 20px;
        border-radius: 3px;
        background: linear-gradient(90deg, #74a3ff 0%, #3876f0 100%);
      }
    }
  }

  &__calendar {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 10px;
    width: fit-content;
  }

  &__day {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 4px;
  }

  &__day-label {
    font-size: 12px;
    line-height: 13px;
    font-weight: 500;
    text-align: center;
    color: rgba($c-black-15, 0.2);
  }

  &__day-check {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 15px;
    height: 15px;
    border-radius: 15px;
    background: rgba($c-black-15, 0.15);
  }

  &__day-check--today {
    .checkmark {
      width: 8px;
      height: 8px;
      border-radius: 8px;
      background: linear-gradient(
        135deg,
        rgba(255, 113, 36, 1) 0%,
        rgba(208, 74, 0, 1) 100%
      );
    }
  }

  &__day-check--today + &__day-label {
    color: $c-black-15;
  }

  &__day-check--active {
    filter: drop-shadow(0px 0px 6px rgba(#ff8d50, 0.8));
    background: linear-gradient(
      135deg,
      rgba(255, 113, 36, 1) 0%,
      rgba(208, 74, 0, 1) 100%
    );

    .checkmark {
      width: 5px;
      height: 8px;
      border-radius: 0;
      border-bottom: 2px solid $c-white;
      border-right: 2px solid $c-white;
      transform: rotate(45deg);
      margin-top: -3px;
    }
  }
}
</style>
